
import type { ChatMessage, ConversationState } from './types/parser';

export class ConversationMemory {
  private messages: ChatMessage[] = [];
  private state: ConversationState = {
    attendeeName: undefined,
    dateTime: undefined,
    action: undefined,
    isComplete: false,
    lastProcessedTime: Date.now()
  };
  private readonly MAX_LENGTH: number;

  constructor(maxLength: number = 50) {
    this.MAX_LENGTH = maxLength;
    
    // Get current date and time in different formats for context
    const now = new Date();
    const isoDate = now.toISOString();
    const humanReadableDate = now.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });

    // Initialize with enhanced system message including current time context
    this.add({
      role: 'system',
      content: `Current time context: ${humanReadableDate} (${isoDate})

You are a calendar assistant that MUST maintain context between messages.
CRITICAL: Always parse time expressions and maintain attendee names across the conversation.
Format dates as ISO strings. Example: 2024-02-21T16:00:00`,
      timestamp: Date.now()
    });
  }

  add(message: ChatMessage) {
    this.messages.push(message);
    
    // Trim if exceeding max length, but keep system message
    if (this.messages.length > this.MAX_LENGTH) {
      const systemMessage = this.messages.find(m => m.role === 'system');
      const removed = this.messages.length - this.MAX_LENGTH;
      console.log(`Trimming conversation memory, removing ${removed} oldest messages`);
      this.messages = systemMessage 
        ? [systemMessage, ...this.messages.slice(-(this.MAX_LENGTH - 1))]
        : this.messages.slice(-this.MAX_LENGTH);
    }

    // Update state from message metadata
    if (message.metadata) {
      this.state = {
        ...this.state,
        attendeeName: message.metadata.attendeeName || this.state.attendeeName,
        dateTime: message.metadata.dateTime || this.state.dateTime,
        action: message.metadata.action || this.state.action,
        isComplete: message.metadata.isComplete || this.state.isComplete,
        lastProcessedTime: Date.now()
      };
      
      console.log('Updated conversation state:', {
        attendeeName: this.state.attendeeName,
        hasDateTime: !!this.state.dateTime,
        action: this.state.action,
        isComplete: this.state.isComplete
      });
    }
  }

  getAll(): ChatMessage[] {
    return this.messages;
  }

  getState(): ConversationState {
    return { ...this.state };
  }

  getContext(): string {
    return this.messages
      .filter(msg => msg.role !== 'system') // Exclude system message from context string
      .map(msg => `${msg.role}: ${msg.content}`)
      .join('\n');
  }

  getSystemMessage(): ChatMessage | undefined {
    return this.messages.find(msg => msg.role === 'system');
  }

  clear() {
    // Store the current time context
    const currentTime = this.messages.find(msg => msg.role === 'system')?.content || '';
    
    // Clear messages but maintain system message with updated time
    const now = new Date();
    const isoDate = now.toISOString();
    const humanReadableDate = now.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });

    this.messages = [{
      role: 'system',
      content: `Current time context: ${humanReadableDate} (${isoDate})

You are a calendar assistant that MUST maintain context between messages.
CRITICAL: Always parse time expressions and maintain attendee names across the conversation.
Format dates as ISO strings. Example: 2024-02-21T16:00:00`,
      timestamp: Date.now()
    }];

    this.state = {
      attendeeName: undefined,
      dateTime: undefined,
      action: undefined,
      isComplete: false,
      lastProcessedTime: Date.now()
    };
    
    console.log('Conversation memory cleared, system message updated with current time');
  }

  size(): number {
    return this.messages.length;
  }
}

